import { useCallback } from "react";
import { Toggle } from "@narmi/design_system";
import { GenericForm, modules } from "byzantine";
import cc from "classcat";
import {
  AccountAlertForm,
  AlertConfigurableField,
  LoanAlertForm,
} from "byzantine/src/dbbl/businessLogic/modules/alerts";

import { formAdapters } from "../../../../src";
import { useTriggerGuideForceReset } from "../../../../src/modules/guides/hooks";
import DeliveryChannels from "../fields/DeliveryChannels";
import AmountQueryField from "../fields/AmountQueryField";
import SubscribedAccountsField from "../fields/SubscribedAccountsField";
import useDisableDeliveryChannels from "../hooks/useDisableDeliveryChannels";
import MultiWeekdaySelector from "../fields/MultiWeekdaySelector";
import DueDateSelector from "../fields/DueDateSelector";
import styles from "./AlertForm.module.scss";

interface AlertFormProps {
  alertKey: API.AlertKey;
}

const AlertForm = ({ alertKey }: AlertFormProps) => {
  const triggerGuideForceReset = useTriggerGuideForceReset();
  const {
    isActive,
    toggleAlert,
    loading,
    title,
    description,
    form,
    configurationOptions,
  } = modules.alerts.useAlert(alertKey, {
    onSaveSuccess: triggerGuideForceReset,
  });
  useDisableDeliveryChannels(alertKey);

  const renderConfigOptions = useCallback(
    (option: AlertConfigurableField) => {
      switch (option.fieldName) {
        case "subscribedAccounts": {
          return (
            <div key={option.fieldKey} className={styles.field}>
              <SubscribedAccountsField option={option} alertKey={alertKey} />
            </div>
          );
        }
        case "thresholdAmount": {
          return (
            <div key={option.fieldKey} className={styles.field}>
              <AmountQueryField
                option={option}
                alertKey={alertKey}
                fieldName="thresholdAmount"
              />
            </div>
          );
        }
        case "transactionQuery": {
          return (
            <div key={option.fieldKey} className={styles.field}>
              <AmountQueryField
                option={option}
                alertKey={alertKey}
                fieldName="transactionQuery"
              />
            </div>
          );
        }
        case "deliveryChannels": {
          return (
            <div
              key={option.fieldKey}
              className={cc({
                [styles.singleField]: configurationOptions.length === 1,
              })}
            >
              <DeliveryChannels
                alertKey={alertKey}
                {...formAdapters.fieldWithOnChange(form, "deliveryChannels")}
              />
            </div>
          );
        }
        case "rrule": {
          const { value, ...rruleProps } = formAdapters.fieldWithOnChange(
            form as GenericForm<AccountAlertForm>,
            "rrule"
          );
          return (
            <div key={option.fieldKey} className={styles.field}>
              <MultiWeekdaySelector selectedDays={value} {...rruleProps} />
            </div>
          );
        }
        case "reminderDaysOffset": {
          return (
            <div key={option.fieldKey} className={styles.field}>
              <DueDateSelector
                label={option.fieldLabel}
                {...formAdapters.select(
                  form as GenericForm<LoanAlertForm>,
                  "reminderDaysOffset"
                )}
              />
            </div>
          );
        }

        default:
          return <></>;
      }
    },
    [form]
  );
  return (
    <div className={styles.container}>
      <div className={styles.headingContainer}>
        <span className={styles.heading}>{title}</span>
        <Toggle
          isActive={isActive}
          disabled={loading}
          onChange={toggleAlert}
        />
      </div>
      {description && <div className={styles.subHeading}>{description}</div>}
      {isActive && (
        <div className={styles.configWrapperContainer}>
          {configurationOptions.map(renderConfigOptions)}
        </div>
      )}
    </div>
  );
};

export default AlertForm;
